/**
 * The global API envelope, unwrapped in ONE place.
 *
 * Every alert endpoint answers `{ metadata, data, error }`. Components and hooks
 * must never reach into that shape themselves — they receive normalized page
 * DTOs from the request layer, so a change to the envelope touches this file and
 * nothing else.
 *
 * `metadata` is intentionally generic: Queue carries opaque cursors, History
 * carries offset/page counts, and squashing both into one pagination type is
 * what makes a mutable queue skip rows.
 */
import { AxiosError, type AxiosResponse } from "axios";
import i18n from "@/shared/lib/i18n/index.ts";

/** The error member of the envelope. Shape is best-effort — never trusted blindly. */
export interface ApiErrorPayload {
  code?: string;
  message?: string;
  detail?: string;
  [key: string]: unknown;
}

export interface ApiEnvelope<TData, TMetadata> {
  metadata: TMetadata;
  data: TData;
  error: ApiErrorPayload | null;
}

/**
 * A typed failure from an alert endpoint. Carries what the UI actually has to
 * branch on — the status, a safely-surfaced backend message, and the parsed
 * `Retry-After` for 429s — instead of leaving every caller to re-parse an
 * `AxiosError`.
 */
export class AlertApiError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  /** Seconds to wait, parsed from `Retry-After` on a 429. Null otherwise. */
  readonly retryAfterSeconds: number | null;
  readonly payload: ApiErrorPayload | null;

  constructor(
    message: string,
    status: number,
    retryAfterSeconds: number | null = null,
    payload: ApiErrorPayload | null = null,
  ) {
    super(message);
    this.name = "AlertApiError";
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
    this.payload = payload;
  }

  /** 401/403 — handled by the app's standard auth flow, not by a retry button. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** Machine-readable failure code, when the backend supplied one. */
  get code(): string | null {
    return typeof this.payload?.code === "string" ? this.payload.code : null;
  }
}

/**
 * The workflow failures the review pane must handle DIFFERENTLY. Each one
 * demands its own recovery, and none of them may be blind-retried — a retry
 * would either apply a decision on top of someone else's, or replay an intent
 * the operator never repeated.
 *
 * Codes are matched first (authoritative), with the status as a fallback for a
 * backend that omits one.
 */
export type AlertWorkflowFailure =
  /** Someone else changed the alert after this operator loaded it. */
  | "alert_state_changed"
  /** The same idempotency key arrived with a different body. */
  | "idempotency_conflict"
  /** `If-Match` was missing or stale; reload to get the current version. */
  | "workflow_version_required"
  /** Not a legal transition from the current workflow state. */
  | "invalid_transition"
  /** The alert is gone; the open detail is stale. */
  | "alert_not_found"
  /** The operator's role does not allow this command (403). */
  | "action_not_permitted"
  | "unknown";

export const alertWorkflowFailure = (error: unknown): AlertWorkflowFailure => {
  if (!(error instanceof AlertApiError)) return "unknown";
  switch (error.code) {
    case "alert_state_changed":
    case "idempotency_conflict":
    case "workflow_version_required":
    case "invalid_transition":
    case "alert_not_found":
    case "action_not_permitted":
      return error.code;
  }
  // No code supplied — fall back to the status. A bare 409 is treated as a
  // state change rather than an idempotency clash: reconciling is always safe,
  // whereas assuming a key clash would hide a real concurrent decision.
  switch (error.status) {
    case 409:
      return "alert_state_changed";
    case 428:
      return "workflow_version_required";
    case 422:
      return "invalid_transition";
    case 404:
      return "alert_not_found";
    // Reopen is restricted to Owner/Admin/Supervisor and the user payload
    // carries no role, so the control cannot be hidden from those who lack it —
    // a refusal has to read as a permission answer, not as "unknown".
    case 403:
      return "action_not_permitted";
    default:
      return "unknown";
  }
};

/** Operator-facing copy for a workflow failure. Plain operational language. */
export const alertWorkflowFailureMessage = (
  failure: AlertWorkflowFailure,
  fallback: string,
): string => {
  switch (failure) {
    case "alert_state_changed":
      return i18n.t("errors.workflow.stateChanged", { ns: "alerts" });
    case "idempotency_conflict":
      return i18n.t("errors.workflow.idempotencyConflict", { ns: "alerts" });
    case "workflow_version_required":
      return i18n.t("errors.workflow.versionRequired", { ns: "alerts" });
    case "invalid_transition":
      return fallback || i18n.t("errors.workflow.invalidTransition", { ns: "alerts" });
    case "alert_not_found":
      return i18n.t("errors.workflow.notFound", { ns: "alerts" });
    case "action_not_permitted":
      return i18n.t("errors.workflow.notPermitted", { ns: "alerts" });
    default:
      return fallback || i18n.t("errors.workflow.generic", { ns: "alerts" });
  }
};

/**
 * `Retry-After` is either a delay in seconds or an HTTP date. Both are accepted;
 * anything else yields null rather than a bogus delay.
 */
export const parseRetryAfter = (header: unknown, now: number = Date.now()): number | null => {
  if (typeof header !== "string" || header.trim() === "") return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, Math.round(seconds));
  const at = Date.parse(header);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.round((at - now) / 1000));
};

/** Pull a human-readable message out of whatever the backend sent. */
const messageFrom = (payload: ApiErrorPayload | null | undefined, fallback: string): string => {
  if (!payload) return fallback;
  const candidate = payload.message ?? payload.detail;
  return typeof candidate === "string" && candidate.trim() !== "" ? candidate : fallback;
};

/**
 * Unwrap an envelope response into `{ data, metadata }`, or throw an
 * {@link AlertApiError}. A 200 carrying a non-null `error` member is a failure
 * too — the backend reports validation problems that way.
 */
export const unwrapEnvelope = <TData, TMetadata>(
  response: AxiosResponse<ApiEnvelope<TData, TMetadata>>,
): { data: TData; metadata: TMetadata } => {
  const body = response.data;
  if (body?.error) {
    throw new AlertApiError(
      messageFrom(body.error, i18n.t("errors.request.notCompleted", { ns: "alerts" })),
      response.status,
      parseRetryAfter(response.headers?.["retry-after"]),
      body.error,
    );
  }
  return { data: body.data, metadata: body.metadata };
};

/** Normalize any thrown value from an alert request into an {@link AlertApiError}. */
export const toAlertApiError = (error: unknown): AlertApiError => {
  if (error instanceof AlertApiError) return error;
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 0;
    const payload = (error.response?.data as { error?: ApiErrorPayload } | undefined)?.error ?? null;
    return new AlertApiError(
      messageFrom(payload, error.message || i18n.t("errors.request.failed", { ns: "alerts" })),
      status,
      parseRetryAfter(error.response?.headers?.["retry-after"]),
      payload,
    );
  }
  return new AlertApiError(
    error instanceof Error ? error.message : i18n.t("errors.request.failed", { ns: "alerts" }),
    0,
  );
};
